import { FC, useEffect, useState } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store';
import { QcsButton } from '../../common/basic/QcsButton';
import { TableRowButton } from '../../common/basic/TableRowButton';
import { QcsTypography } from '../../common/basic/QcsTypography';
import { FetchState } from '../../../store/fetchState';
import { QcsTableBody } from '../../common/basic/QcsTableBody';
import { QcsTableCell } from '../../common/basic/QcsTableCell';
import { QcsTableHead } from '../../common/basic/QcsTableHead';
import { QcsTableRow } from '../../common/basic/QcsTableRow';
import { QcsPaper } from '../../common/basic/QcsPaper';
import { QcsTableContainer } from '../../common/basic/QcsTableContainer';
import { GridEmpty } from '../../common/grid/GridEmpty';
import { GridError } from '../../common/grid/GridError';
import { GridLoading } from '../../common/grid/GridLoading';
import { appTrainingLangListActions, getAppTrainingLangList, selectAppTrainingLangList } from '../../../store/entities/appTrainingLangList';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { QcsTable } from '../../common/basic/QcsTable';
import { TrainingLanguageDetailModal } from './TrainingLanguageDetailModal';
import { LanguageDto } from '@api/models/settings/LanguageDto';
import { getLanguageName } from '../../../utils/i18n';
import { codeListService } from '@api/services/services';

const HeaderContainer = styled.div(
  (): CSSObject => ({
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1rem',
    marginBottom: '0.5rem',
  })
);

export const TrainingLanguages: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const appTrainingLangList = useAppSelector(selectAppTrainingLangList);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (appTrainingLangList.state === FetchState.None) {
      dispatch(appTrainingLangListActions.reload(false));
    }
  }, [appTrainingLangList.state, dispatch]);

  useEffect(() => {
    if (appTrainingLangList.state === FetchState.None) {
      dispatch(getAppTrainingLangList());
    }
  }, [appTrainingLangList.state, dispatch]);

  const reloadTrainingLangList = () => {
    dispatch(appTrainingLangListActions.reload(true));
  };

  const handleAddClick = () => {
    setAdding(true);
  };

  const handleClose = () => {
    setAdding(false);
  };

  const handleDelete = (language: LanguageDto) => async () => {
    await codeListService.deleteById(language.code);
    reloadTrainingLangList();
  };

  return (
    <>
      <TrainingLanguageDetailModal
        open={adding}
        onClose={handleClose}
        existingLanguageCodes={appTrainingLangList.data?.content.map((x) => x.code) ?? []}
        reloadTrainingLangList={reloadTrainingLangList}
      />

      <HeaderContainer>
        <QcsTypography variant="h5">{t('admin.settings.trainingLang.title')}</QcsTypography>
        <QcsButton variant="contained" onClick={handleAddClick}>
          {t('admin.settings.trainingLang.addLanguage')}
        </QcsButton>
      </HeaderContainer>

      <QcsPaper>
        <QcsTableContainer>
          <QcsTable>
            <QcsTableHead>
              <QcsTableRow>
                <QcsTableCell>{t('admin.settings.trainingLang.language')}</QcsTableCell>
                <QcsTableCell sx={{ width: '5rem' }}>{t('admin.settings.trainingLang.delete')}</QcsTableCell>
              </QcsTableRow>
            </QcsTableHead>
            <QcsTableBody>
              {appTrainingLangList.state === FetchState.Loaded &&
                appTrainingLangList.data?.content.map((item, index) => (
                  <QcsTableRow key={index} hover={true}>
                    <QcsTableCell>
                      <img src={'/flags/' + item.flag + '.svg'} height="20" /> {getLanguageName(t, item)}
                    </QcsTableCell>
                    <QcsTableCell align="center">
                      <TableRowButton onClick={handleDelete(item)}>
                        <DeleteForeverIcon color="error" />
                      </TableRowButton>
                    </QcsTableCell>
                  </QcsTableRow>
                ))}
            </QcsTableBody>
          </QcsTable>
          {appTrainingLangList.state === FetchState.Loading ? (
            <GridLoading />
          ) : appTrainingLangList.state === FetchState.Error ? (
            <GridError />
          ) : appTrainingLangList.data?.content.length === 0 ? (
            <GridEmpty />
          ) : null}
        </QcsTableContainer>
      </QcsPaper>
    </>
  );
};
